import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useMemo, useRef, useState } from "react";
import { useDebounceCallback, useEventListener, useResizeObserver } from "./hooks";
import { computeRatio, getGapSize, handleExtractSize, updateScrollElementStyle, updateScrollPosition } from "./utils";
import ThumbBar from "./ThumbBar";
import { jsx as _jsx } from "react/jsx-runtime";
var initialSize = {
  CW: 0,
  SW: 0,
  CH: 0,
  SH: 0,
  PT: 0,
  PR: 0,
  PB: 0,
  PL: 0
};
var initialAction = {
  pinX: false,
  pinY: false,
  lastST: 0,
  lastSL: 0,
  startX: 0,
  startY: 0
};
export default function useScrollbar(scrollBox, _ref) {
  var _ref$trackGap = _ref.trackGap,
    trackGap = _ref$trackGap === void 0 ? 16 : _ref$trackGap,
    trackStyle = _ref.trackStyle,
    thumbStyle = _ref.thumbStyle,
    minThumbSize = _ref.minThumbSize,
    autoHide = _ref.autoHide;
  var isGlobal = scrollBox === window;
  var containerRef = useMemo(function () {
    if (isGlobal) {
      return {
        current: document.documentElement
      };
    }
    return scrollBox;
  }, [isGlobal, scrollBox]);
  var horizontalRef = useRef(null);
  var verticalRef = useRef(null);
  var _useState = useState(initialSize),
    _useState2 = _slicedToArray(_useState, 2),
    boxSize = _useState2[0],
    updateBoxSize = _useState2[1];
  var _useState3 = useState(initialAction),
    _useState4 = _slicedToArray(_useState3, 2),
    action = _useState4[0],
    updateAction = _useState4[1];
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    barVisible = _useState6[0],
    updateBarVisible = _useState6[1];
  var hideScrollbar = function hideScrollbar() {
    return autoHide && updateBarVisible(false);
  };
  var delayHideScrollbar = useDebounceCallback(hideScrollbar, {
    wait: 1000
  });
  var CW = boxSize.CW,
    SW = boxSize.SW,
    CH = boxSize.CH,
    SH = boxSize.SH;
  var showHorizontalBar = SW - CW > 0;
  var showVerticalBar = SH - CH > 0;
  var _getGapSize = getGapSize(trackGap, showHorizontalBar && showVerticalBar),
    _getGapSize2 = _slicedToArray(_getGapSize, 4),
    startX = _getGapSize2[0],
    gapX = _getGapSize2[1],
    startY = _getGapSize2[2],
    gapY = _getGapSize2[3];
  var updateLayerThrottle = useDebounceCallback(function () {
    updateBarVisible(true);
    delayHideScrollbar();
    updateScrollElementStyle(containerRef.current, horizontalRef.current, verticalRef.current, gapX, gapY, minThumbSize);
  }, {
    maxWait: 8,
    leading: true
  });
  useEventListener("mousemove", function (evt) {
    if (action.pinX) {
      var horizontalRatio = computeRatio(SW, CW, gapX, minThumbSize).ratio;
      updateScrollPosition(containerRef.current, Math.floor((evt.clientX - action.startX) * (1 / horizontalRatio) + action.lastSL), true);
    }
    if (action.pinY) {
      var verticalRatio = computeRatio(SH, CH, gapY, minThumbSize).ratio;
      updateScrollPosition(containerRef.current, Math.floor((evt.clientY - action.startY) * (1 / verticalRatio) + action.lastST));
    }
  });
  useEventListener("mouseup", function () {
    return updateAction(initialAction);
  });
  useResizeObserver(containerRef, updateLayerNow);
  function updateLayerNow() {
    if (containerRef.current) {
      updateBoxSize(handleExtractSize(containerRef.current));
      updateLayerThrottle();
    }
  }
  var horizontalBar = showHorizontalBar && /*#__PURE__*/_jsx(ThumbBar, {
    visible: barVisible,
    isGlobal: isGlobal,
    trackStyle: trackStyle,
    thumbStyle: thumbStyle,
    minThumbSize: minThumbSize,
    start: startX,
    gap: gapX,
    horizontal: true,
    pin: action.pinX,
    trackRef: horizontalRef,
    boxSize: boxSize,
    update: updateAction
  });
  var verticalBar = showVerticalBar && /*#__PURE__*/_jsx(ThumbBar, {
    visible: barVisible,
    isGlobal: isGlobal,
    trackStyle: trackStyle,
    thumbStyle: thumbStyle,
    minThumbSize: minThumbSize,
    start: startY,
    gap: gapY,
    pin: action.pinY,
    trackRef: verticalRef,
    boxSize: boxSize,
    update: updateAction
  });
  return [horizontalBar, verticalBar, updateLayerNow, updateLayerThrottle, hideScrollbar];
}